import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

// Minimal typed wrapper for the beta supabase.auth.oauth namespace.
type OAuthDetails = {
  client?: { name?: string; client_uri?: string; redirect_uris?: string[] } | null;
  scope?: string;
  redirect_url?: string;
  redirect_to?: string;
};
type OAuthResult = { data: OAuthDetails | null; error: { message: string } | null };
type OAuthNs = {
  getAuthorizationDetails(id: string): Promise<OAuthResult>;
  approveAuthorization(id: string): Promise<OAuthResult>;
  denyAuthorization(id: string): Promise<OAuthResult>;
};
const oauth = (supabase.auth as unknown as { oauth: OAuthNs }).oauth;

export const Route = createFileRoute("/.lovable/oauth/consent")({
  // Browser-only: Supabase reads the session from localStorage, which SSR can't see.
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id:
      typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      // location.href is already pathname + search (a string); location.search is a parsed object.
      throw redirect({ to: "/login", search: { next: location.href } as never });
    }
  },
  loader: async ({ location }) => {
    // location.href is "pathname?search" — parse the query out of it.
    const queryIdx = location.href.indexOf("?");
    const qs = queryIdx >= 0 ? location.href.slice(queryIdx + 1) : "";
    const authorizationId = new URLSearchParams(qs).get("authorization_id") ?? "";
    const { data, error } = await oauth.getAuthorizationDetails(authorizationId);
    if (error) throw new Error(error.message);
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate } as never);
    return data;
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-md px-4 py-16 text-sm">
      <div className="mb-6 flex justify-center"><Logo /></div>
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
        <h1 className="font-display text-lg font-semibold">Authorization error</h1>
        <p className="mt-2 text-muted-foreground">
          Could not load this authorization request: {String((error as Error)?.message ?? error)}
        </p>
      </div>
    </main>
  ),
});

function Consent() {
  const details = Route.useLoaderData() as OAuthDetails | null;
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const { data, error } = approve
      ? await oauth.approveAuthorization(authorization_id)
      : await oauth.denyAuthorization(authorization_id);
    if (error) {
      setBusy(false);
      setError(error.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an app";

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-stretch justify-center px-4 py-10">
      <div className="mb-6 flex justify-center"><Logo /></div>
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
        <h1 className="font-display text-xl font-semibold">
          Connect {clientName} to MissionReady
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {clientName} will be able to call MissionReady's enabled tools while you are signed in.
          This does not bypass MissionReady's permissions or backend policies — tools act as you
          and respect row-level security.
        </p>

        {details?.scope && (
          <div className="mt-4 rounded-lg bg-muted/40 p-3 text-xs">
            <div className="mb-1 font-medium">Requested scope</div>
            <div className="text-muted-foreground">{details.scope}</div>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={busy}
            onClick={() => decide(false)}
          >
            Cancel connection
          </Button>
          <Button
            type="button"
            className="flex-1"
            disabled={busy}
            onClick={() => decide(true)}
          >
            {busy ? "Working…" : "Approve"}
          </Button>
        </div>
      </div>
    </main>
  );
}
